import { CloseIcon } from '@/Components/Common/Icons';
import { Modal } from 'antd';
import PropTypes from 'prop-types';
import { forwardRef, useContext, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { DashboardModalContext } from './DashboardContext';

export const useDashboardModal = () => useContext(DashboardModalContext);

export const useOnDashboardModalOpen = (onOpen) => {
  const dashboardModalContext = useDashboardModal();
  const isOpen = Boolean(dashboardModalContext?.isOpen);

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen, onOpen]);
};

const StyledDashboardModal = styled(Modal)`
  &.ant-modal {
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    padding: 40px;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 6px 6px 20px rgba(4, 76, 123, 0.2);
  }

  .ant-modal-header {
    margin-bottom: 24px;
    padding: 0 0 24px;
    border-bottom: 1px solid #d9d9d9;
    background: transparent;
  }

  .ant-modal-title {
    margin-right: 56px;
  }

  .ant-modal-close {
    top: 30px;
    right: 30px;
    color: #113e57;
  }

  .ant-modal-close-icon {
    font-size: 18px;
    font-weight: 700;
  }

  .ant-modal-body {
    padding: 0;
  }

  @media (max-width: 768px) {
    .ant-modal-content {
      padding: 20px;
    }

    .ant-modal-header {
      margin-bottom: 16px;
      padding-bottom: 16px;
    }
  }
`;

export const DashboardModal = forwardRef(
  (
    {
      children = null,
      open = undefined,
      visible = undefined,
      className = '',
      rootClassName = '',
      onOpen = undefined,
      onClose = undefined,
      onOpenChange = undefined,
      afterOpenChange = undefined,
      ...restProps
    },
    ref
  ) => {
    const {
      destroyOnClose,
      destroyOnHidden,
      afterOpenChange: modalAfterOpenChange,
      ...modalProps
    } = restProps;
    const parentContextValue = useDashboardModal();
    const hasOpenProp = typeof open === 'boolean';
    const hasVisibleProp = typeof visible === 'boolean';
    const isOpen = Boolean(hasOpenProp ? open : visible);
    const mergedRootClassName = ['hs-dashboard-modal', rootClassName].filter(Boolean).join(' ');
    const resolvedDestroyOnHidden =
      typeof destroyOnHidden === 'boolean'
        ? destroyOnHidden
        : typeof destroyOnClose === 'boolean'
          ? destroyOnClose
          : undefined;
    const previousOpenRef = useRef(isOpen);

    useEffect(() => {
      if (hasOpenProp && hasVisibleProp && open !== visible) {
        console.warn(
          '[DashboardModal] Both `open` and `visible` were provided with different values. Using `open`.'
        );
      }
    }, [hasOpenProp, hasVisibleProp, open, visible]);

    useEffect(() => {
      const wasOpen = previousOpenRef.current;

      if (wasOpen !== isOpen) {
        if (isOpen) {
          onOpen?.();
        } else {
          onClose?.();
        }

        onOpenChange?.(isOpen);
      }

      previousOpenRef.current = isOpen;
    }, [isOpen, onClose, onOpen, onOpenChange]);

    const contextValue = useMemo(
      () => ({
        ...parentContextValue,
        isOpen,
      }),
      [isOpen, parentContextValue]
    );

    return (
      <DashboardModalContext.Provider value={contextValue}>
        <StyledDashboardModal
          ref={ref}
          open={isOpen}
          centered={restProps.centered ?? true}
          className={className}
          rootClassName={mergedRootClassName}
          destroyOnHidden={resolvedDestroyOnHidden}
          afterOpenChange={afterOpenChange ?? modalAfterOpenChange}
          closeIcon={<CloseIcon style={{ height: '16px', width: '16px' }} />}
          {...modalProps}
        >
          {children}
        </StyledDashboardModal>
      </DashboardModalContext.Provider>
    );
  }
);

DashboardModal.displayName = 'DashboardModal';

DashboardModal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  open: PropTypes.bool,
  visible: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onOpenChange: PropTypes.func,
  afterOpenChange: PropTypes.func,
};

DashboardModal.info = Modal.info;
DashboardModal.success = Modal.success;
DashboardModal.error = Modal.error;
DashboardModal.warning = Modal.warning;
DashboardModal.confirm = Modal.confirm;
DashboardModal.destroyAll = Modal.destroyAll;
DashboardModal.useModal = Modal.useModal;
